import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createSharedCameraStreamsApi } from '../../services/sharedCameraStreamsApi';
import { createSharedCameraStreams } from '../../services/SharedCameraStreams';

const closed = { open: false };
export function useSharedCameraStreams(scope, enabled = true, onOpenMedia) {
  // Logout suspension follows the login scope across route changes. Recovery
  // replaces the manager; callbacks already handed to Social stay obsolete.
  const lifetime = useMemo(() => ({ scope, suspended: null }), [scope]);
  const lifetimeRef = useRef(lifetime); lifetimeRef.current = lifetime;
  const [generation, setGeneration] = useState(0);
  const binding = useMemo(() => ({ scope, enabled, lifetime, generation }), [scope, enabled, lifetime, generation]);
  const bindingRef = useRef(binding); bindingRef.current = binding;
  const [snapshot, setSnapshot] = useState(null);
  useLayoutEffect(() => {
    if (!enabled || lifetime.suspended || !scope?.identity || !scope?.ownerId || !scope?.backendService) return undefined;
    let active = true, manager;
    const isCurrent = () => active && bindingRef.current === binding && binding.enabled && !lifetime.suspended;
    try {
      const api = createSharedCameraStreamsApi({ ...scope, isCurrent });
      manager = createSharedCameraStreams({ api, isCurrent, mediaOrigin: scope.mediaOrigin });
    } catch { return undefined; }
    setSnapshot({ binding, manager, state: manager.getState() });
    const unsubscribe = manager.subscribe(state => setSnapshot({ binding, manager, state }));
    const background = () => manager.close();
    const visibility = () => { if (document.visibilityState === 'hidden') background(); };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', background);
    return () => {
      active = false; unsubscribe(); manager.dispose();
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', background);
    };
  }, [scope, enabled, binding, lifetime]);
  const current = enabled && !lifetime.suspended && snapshot?.binding === binding ? snapshot : null;
  const manager = current?.manager;
  const suspendForLogout = () => {
    if (lifetimeRef.current !== lifetime || lifetime.suspended) return () => {};
    const suspension = {};
    // This mutable fence precedes disposal, React rendering and the first await.
    lifetime.suspended = suspension;
    manager?.dispose();
    setSnapshot(null);
    return () => {
      if (lifetimeRef.current !== lifetime || lifetime.suspended !== suspension) return;
      lifetime.suspended = null;
      setGeneration(value => value + 1);
    };
  };
  return { available: Boolean(manager), state: current?.state || closed,
    suspendForLogout,
    openMedia: () => {
      if (manager && bindingRef.current === binding && lifetimeRef.current === lifetime
        && !lifetime.suspended && enabled && document.visibilityState !== 'hidden') onOpenMedia?.();
    },
    open: () => { if (document.visibilityState !== 'hidden') manager?.open(); },
    close: () => manager?.close(), load: more => manager?.load(more), select: id => manager?.select(id),
    watch: id => { if (document.visibilityState !== 'hidden') void manager?.watch(id); },
    attachVideo: (video, stream) => manager?.attachVideo(video, stream),
    play: name => manager?.play(name), tab: view => manager?.tab(view), clearSelection: () => manager?.clearSelection(), playbackError: () => manager?.playbackError(current?.state.stream || current?.state.url) };
}
